import type { ReactNode, UIEvent } from 'react';

import { Button } from '@shared/ui/button';

import {
  NOTIFICATION_POPOVER_CONTENT_PX,
  NOTIFICATION_SCROLL_END_PX,
} from './notification-popover.constant';
import { NOTIFICATION_POPOVER_TEXT } from './notification-popover.text';

export interface NotificationPopoverListProps {
  children: ReactNode;
  showLoading?: boolean;
  onScrollEnd?(): void;
  /** 위 고정 줄의 "모두읽기". 없으면 줄을 그리지 않는다 */
  onReadAll?(): void;
  /** 읽지 않은 알림이 없으면 누를 수 없다. 기본 true */
  canReadAll?: boolean;
}

/**
 * 내부 부품 `_NotificationPopover/List`: 위 고정 줄("모두읽기", 오른쪽) + 알림 줄 자리(세로 스크롤) + Loading 줄.
 * 전체 크기는 그대로 — 고정 줄만큼 스크롤 자리가 줄어든다
 */
export function NotificationPopoverList({
  children,
  showLoading = false,
  onScrollEnd,
  onReadAll,
  canReadAll = true,
}: NotificationPopoverListProps) {
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const box = event.currentTarget;
    if (box.scrollHeight - box.scrollTop - box.clientHeight <= NOTIFICATION_SCROLL_END_PX) {
      onScrollEnd?.();
    }
  };
  return (
    <div style={NOTIFICATION_POPOVER_CONTENT_PX} className="flex flex-col">
      {onReadAll ? (
        <div data-slot="toolbar" className="flex shrink-0 justify-end pb-1">
          <Button variant="ghost" size="2xs" disabled={!canReadAll} onClick={onReadAll}>
            {NOTIFICATION_POPOVER_TEXT.readAll}
          </Button>
        </div>
      ) : null}
      <div
        data-slot="scroll"
        onScroll={handleScroll}
        className="scrollbar-none flex min-h-0 flex-1 flex-col overflow-y-auto"
      >
        <div role="list" className="flex flex-col *:last:*:border-b-0">
          {children}
        </div>
        {showLoading ? (
          <p
            aria-live="polite"
            className="flex h-(--size-row) shrink-0 items-center justify-center pt-2-5 typo-caption text-fg-muted"
          >
            {NOTIFICATION_POPOVER_TEXT.loading}
          </p>
        ) : null}
      </div>
    </div>
  );
}
